import React, { useCallback, useEffect, useMemo, useState } from "react";
import { callAPI } from "../../helper/apiUtils";
import LanguageContext from "../../helper/LanguageContext";
import { API_ROOT, KJUUP_COLORS } from "../../helper/constants";
import DocViewer from "./DocViewer";
import _ from "lodash";
import * as moment from "moment";
import "moment/locale/de";
import "moment/locale/en-gb";
import "moment/locale/fr";
import "moment/locale/it";
import "react-circular-progressbar/dist/styles.css";
import ImageSection from "../../components/mediaSection/ImageSection";
import DocsSection from "../../components/mediaSection/DocsSection";
import MediaSelectionBar from "../../components/mediaSection/MediaSelectionBar";
import MediaAutoSyncSettings from "../../components/mediaSection/MediaAutoSyncSettings";
import { AttachmentPreviewModal } from "../../components/EmailDetailView";
import TrialWarningBanner from "../../components/TrialWarningBanner";
import EmailEmptyState from "../../components/EmailEmptyState";
import { useDispatch, useSelector } from "react-redux";
import FullScreenImageViewer from "../../components/FullScreenImageViewer";
import MaterialIcon from "../../components/MaterialIcon";
import teamsLogo from "../../Icons/Teams.png";
import { canShowEmailCalendarIntegration } from "../../utils/kjuupIoEntitlement";
import { resolveStripeFlags } from "../../utils/stripeFeature";
import { getLocaleForLanguage } from "../../utils/composerUtils";
import {
  pickDownloadDirectory,
  itemMatchesMediaTab,
} from "../../utils/downloadMediaToFolder";
import {
  applyDownloadJobToSections,
  dismissMediaDownload,
  startMediaDownload,
  useMediaDownload,
} from "../../utils/mediaDownloadManager";
import { useHistory } from "react-router-dom";
import {
  OPEN_PROJECTS,
  RIGHT_TOP,
  SET_OPEN_SIDE_BAR,
  TOGGLE_PROJECT,
} from "../../store";
import "./BuildingMediaDocs.css";

function isImageMime(mime) {
  return String(mime || "").toLowerCase().startsWith("image/");
}

function isOpenableEmailDoc(item) {
  const name = String((item && item.filename) || "").trim().toLowerCase();
  if (!name || name === "attachment" || name === "noname" || name === "untitled") return false;
  if (!item.attachmentId) return false;
  const mime = String((item && item.mimeType) || "").toLowerCase();
  if (name === "winmail.dat" || mime.includes("tnef")) return false;
  if (mime === "text/calendar" || name.endsWith(".ics")) return false;
  if (mime.includes("pkcs7-signature") || name.endsWith(".p7s") || name.endsWith(".p7m")) return false;
  return true;
}

// Download-job kind for a media tab (0 = Media, 1 = Docs, 2 = All).
function mediaTabKind(tab) {
  if (tab === 2) return "all";
  return tab === 0 ? "media" : "docs";
}

function normalizeChatItem(item) {
  return { ...item, source: item.source || "chat" };
}

// Which items a source tab shows: Kjuup = the room's own chats, Teams = the user's Teams chats
// filed under the room (GET /teams/projects/:id/media), E-Mail = the room's mail attachments.
function matchesSourceFilter(item, filter) {
  if (filter === "chat") return item.source !== "email" && item.source !== "teams";
  if (filter === "email") return item.source === "email";
  if (filter === "teams") return item.source === "teams";
  return true;
}

function normalizeEmailItem(item) {
  const image = isImageMime(item.mimeType);
  return {
    id: `email-${item.id}`,
    linkId: item.id,
    attachmentId: item.attachmentId,
    source: "email",
    messageType: image ? "image" : "doc",
    attachedFileName: item.filename || "attachment",
    mimeType: item.mimeType,
    createdAt: item.receivedAt,
    senderEmail: item.senderEmail,
    size: item.size,
    fileUrl: null,
    isSelect: false,
    isDownloading: false,
    progress: 0,
  };
}

function groupItemsByMonth(items, locale) {
  const grouping = _.groupBy(items, (el) =>
    moment(el.createdAt).locale(locale).format("MMMM, Y")
  );
  return _.map(grouping, (sectionItems, createdAt) => ({
    createdAt,
    items: sectionItems,
  })).sort((a, b) => {
    const ta = new Date(a.items[0]?.createdAt || 0).getTime();
    const tb = new Date(b.items[0]?.createdAt || 0).getTime();
    return tb - ta;
  });
}

export default function BuildingMediaDocs(props) {
  const { strings, language } = React.useContext(LanguageContext);
  const history = useHistory();
  const dispatch = useDispatch();
  const [value, setValue] = React.useState(2);
  const [chatMedia, setChatMedia] = useState([]);
  const [emailItems, setEmailItems] = useState([]);
  // all | chat | email | teams. `emailOnly` (chats hidden in Kjuup.io) pins the list to email items.
  const [sourceFilter, setSourceFilter] = useState(() =>
    props.viewByOwner ? "chat" : props.emailOnly ? "email" : "all"
  );
  const [emailPreview, setEmailPreview] = useState(null);
  const [viewDoc, setViewDoc] = useState(false);
  const [doc, setDoc] = useState("");
  const [isSelect, setIsSelect] = useState(false);
  const [SelectedItems, setSelectedItems] = useState([]);
  const [emailLoading, setEmailLoading] = useState(false);
  const [sourceMenuOpen, setSourceMenuOpen] = useState(false);
  const sourceMenuRef = React.useRef(null);

  useEffect(() => {
    if (!sourceMenuOpen) return undefined;
    const onPointer = (e) => {
      if (sourceMenuRef.current && !sourceMenuRef.current.contains(e.target)) setSourceMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setSourceMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [sourceMenuOpen]);

  const ViewerImages = useSelector((state) => state.ViewerImages);
  const downloadScopeId = props.project_id ? `building:${props.project_id}` : null;
  const downloadJob = useMediaDownload(downloadScopeId);
  const jobRunning = downloadJob.status === "running";
  const jobOnThisTab =
    jobRunning && downloadJob.kind === mediaTabKind(value);
  const profile = useSelector((state) => state.profile);
  const subscriptionState = useSelector((state) => state.subscription) || {};
  const {
    stripeFeatureEnabled,
    companyStripeFeatureEnabled,
    isKjuupPlusMember,
    stripeUiEnabled,
  } = resolveStripeFlags(profile, subscriptionState);
  const hasActiveKjuupIoPlan = !stripeUiEnabled || !!subscriptionState.active;
  const hasUsedTrial = !!subscriptionState.hasUsedTrial;
  const showCompanyTrialEndedNote = !!subscriptionState.showCompanyTrialEndedNote;
  const emailFeatureEnabled = canShowEmailCalendarIntegration({
    isKjuupPlusMember,
    hasActiveKjuupIoPlan,
    showCompanyTrialEndedNote,
    stripeFeatureEnabled,
    companyStripeFeatureEnabled,
  });
  const isViewingMode = !!props.viewByOwner;
  const emailOnly = !!props.emailOnly && emailFeatureEnabled && !isViewingMode;

  useEffect(() => {
    if (isViewingMode) {
      setSourceFilter("chat");
      return;
    }
    if (emailOnly) {
      if (sourceFilter !== "email") setSourceFilter("email");
      return;
    }
    if (!emailFeatureEnabled && sourceFilter === "email") setSourceFilter("all");
  }, [emailFeatureEnabled, sourceFilter, isViewingMode, emailOnly]);

  const leaveRoomsAndNavigate = (path, state) => {
    dispatch({ type: TOGGLE_PROJECT, payload: false });
    dispatch({ type: OPEN_PROJECTS, openProjects: [] });
    dispatch({ type: RIGHT_TOP, rightTop: "" });
    dispatch({ type: SET_OPEN_SIDE_BAR, payload: "profile" });
    history.push(path, state);
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
    if (jobRunning) return;
    setIsSelect(false);
    setSelectedItems([]);
  };

  useEffect(() => {
    if (!props.project_id) return undefined;
    let cancelled = false;
    const locale = getLocaleForLanguage(language);
    const load = async () => {
      const seen = new Set();
      const items = [];
      for (let pageNum = 1; pageNum <= 40; pageNum += 1) {
        const { data } = await callAPI(
          `/building/${props.project_id}/getMedia?viewByOwner=${Boolean(props.viewByOwner)}&page=${pageNum}&limit=30&messageType=image,video,doc`,
          {},
          "GET"
        );
        if (cancelled) return;
        const batch = data?.status && Array.isArray(data.media) ? data.media : [];
        if (batch.length === 0) break;
        let added = 0;
        batch.forEach((raw) => {
          const item = normalizeChatItem(raw);
          const fileKey = String(item.fileUrl || item.thumbnailUrl || "").split("?")[0];
          const key = String(item.id || fileKey || "");
          if (!key || seen.has(key) || (fileKey && seen.has(`file:${fileKey}`))) return;
          seen.add(key);
          if (fileKey) seen.add(`file:${fileKey}`);
          items.push(item);
          added += 1;
        });
        if (added === 0 || batch.length < 30) break;
      }
      // Photos and documents of the user's Microsoft Teams chats filed under this room (their
      // own only; never in an employee's read-only view). Images stay under Media, docs under Docs.
      if (!props.viewByOwner) {
        for (let pageNum = 1; pageNum <= 40; pageNum += 1) {
          const res = await callAPI(
            `/teams/projects/${props.project_id}/media?page=${pageNum}&limit=30&messageType=image,video,doc`,
            {},
            "GET"
          );
          if (cancelled) return;
          const data = res && res.data;
          const batch = data?.status && Array.isArray(data.media) ? data.media : [];
          batch.forEach((raw) => {
            const key = `teams:${raw.id}`;
            if (!raw.id || seen.has(key)) return;
            seen.add(key);
            items.push(normalizeChatItem({ ...raw, source: "teams" }));
          });
          if (batch.length < 30) break;
        }
      }
      if (!cancelled) setChatMedia(groupItemsByMonth(items, locale));
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [props.project_id, props.viewByOwner, language]);

  const loadEmailAttachments = useCallback(async () => {
    if (isViewingMode || !emailFeatureEnabled || !hasActiveKjuupIoPlan || !props.project_id) {
      setEmailItems([]);
      return;
    }
    setEmailLoading(true);
    try {
      const res = await callAPI(`/email/projects/${props.project_id}/attachments`, {}, "GET");
      const data = res?.data || res;
      if (data && data.status && Array.isArray(data.data)) {
        setEmailItems(data.data.filter(isOpenableEmailDoc).map(normalizeEmailItem));
      } else {
        setEmailItems([]);
      }
    } catch (_) {
      setEmailItems([]);
    } finally {
      setEmailLoading(false);
    }
  }, [emailFeatureEnabled, hasActiveKjuupIoPlan, props.project_id, isViewingMode]);

  useEffect(() => {
    loadEmailAttachments();
  }, [loadEmailAttachments]);

  useEffect(() => {
    if (downloadJob.status !== "completed") return;
    setIsSelect(false);
    setSelectedItems([]);
    setChatMedia((prev) =>
      prev.map((sec) => ({
        ...sec,
        items: (sec.items || []).map((row) => ({
          ...row,
          isSelect: false,
          isDownloading: false,
          downloadDone: false,
          progress: 0,
        })),
      }))
    );
    setEmailItems((prev) =>
      prev.map((row) => ({
        ...row,
        isSelect: false,
        isDownloading: false,
        downloadDone: false,
        progress: 0,
      }))
    );
  }, [downloadJob.status]);

  const media = useMemo(() => {
    const locale = getLocaleForLanguage(language);
    const chatFlat = (chatMedia || []).flatMap((section) =>
      (section.items || []).map((item) => normalizeChatItem(item))
    );
    const emailFlat = !isViewingMode && emailFeatureEnabled && hasActiveKjuupIoPlan ? emailItems : [];
    const combined = [...chatFlat, ...emailFlat].filter((i) => matchesSourceFilter(i, sourceFilter));
    const grouped = groupItemsByMonth(combined, locale);
    return applyDownloadJobToSections(grouped, jobOnThisTab ? downloadJob : null);
  }, [
    chatMedia,
    emailItems,
    sourceFilter,
    language,
    emailFeatureEnabled,
    hasActiveKjuupIoPlan,
    isViewingMode,
    jobOnThisTab,
    downloadJob,
  ]);

  const download = async () => {
    if (jobRunning || SelectedItems.length === 0 || !downloadScopeId) return;
    const picked = await pickDownloadDirectory();
    if (picked.cancelled) return;
    const started = startMediaDownload({
      scopeId: downloadScopeId,
      items: SelectedItems,
      dirHandle: picked.dirHandle,
      kind: mediaTabKind(value),
    });
    if (started.started) {
      setIsSelect(false);
    }
  };

  const _onCheckBoxPress = (value, index, mainIndex) => {
    if (jobRunning) return;
    const section = media[mainIndex];
    if (!section) return;
    const item = section.items[index];
    if (!item) return;
    const nextSelected = item.isSelect
      ? SelectedItems.filter((e) => e.id !== item.id)
      : [...SelectedItems, { ...item, isSelect: true }];

    const toggleInList = (list) =>
      list.map((sec) => ({
        ...sec,
        items: (sec.items || []).map((row) =>
          row.id === item.id ? { ...row, isSelect: !row.isSelect } : row
        ),
      }));

    setChatMedia((prev) => toggleInList(prev));
    setEmailItems((prev) =>
      prev.map((row) =>
        row.id === item.id ? { ...row, isSelect: !row.isSelect } : row
      )
    );
    setSelectedItems(nextSelected);
  };

  const visibleItems = media.flatMap((section) =>
    (section.items || []).filter((item) => itemMatchesMediaTab(item, value))
  );
  const allVisibleSelected =
    visibleItems.length > 0 && visibleItems.every((item) => item.isSelect);

  const toggleSelectAll = () => {
    const select = !allVisibleSelected;
    const matchesTab = (row) => itemMatchesMediaTab(row, value);
    setChatMedia((prev) =>
      prev.map((sec) => ({
        ...sec,
        items: (sec.items || []).map((row) =>
          matchesTab(row) ? { ...row, isSelect: select } : row
        ),
      }))
    );
    setEmailItems((prev) =>
      prev.map((row) => (matchesTab(row) ? { ...row, isSelect: select } : row))
    );
    setSelectedItems(
      select
        ? media
            .flatMap((section) => section.items || [])
            .filter(matchesTab)
            .map((row) => ({ ...row, isSelect: true }))
        : []
    );
  };

  const openEmailItem = (item) =>
    setEmailPreview({
      id: item.attachmentId,
      filename: item.attachedFileName,
      mimeType: item.mimeType,
      contentType: item.mimeType,
    });

  const useEmailTheme = emailFeatureEnabled && !isViewingMode;
  const accent =
    useEmailTheme && sourceFilter === "email"
      ? KJUUP_COLORS.email
      : props.isInternal
        ? KJUUP_COLORS.internal
        : KJUUP_COLORS.base_color;
  // The source dropdown (Alle / Chat / E-Mail / Teams) disappears when only email items are shown.
  // Teams only while the room has files from the user's Teams chats.
  const hasTeamsItems = (chatMedia || []).some((sec) => (sec.items || []).some((i) => i.source === "teams"));
  const showEmailFilter = emailFeatureEnabled && !isViewingMode && !emailOnly;
  const showSourceTabs = !isViewingMode && !emailOnly && (showEmailFilter || hasTeamsItems);
  const showEmailUpsell =
    emailFeatureEnabled && sourceFilter === "email" && !hasActiveKjuupIoPlan;

  // One filter row: type as a segmented control, source in a dropdown, selection as an icon.
  const typeTabs = [
    { key: 2, label: strings.filter_all || "All" },
    { key: 0, label: strings.media_filter_photos || "Photos" },
    { key: 1, label: strings.media_filter_docs || "Documents" },
  ];
  const sourceTabs = [
    { key: "all", label: strings.filter_all || "All", icon: "stacks", color: "#737373" },
    { key: "chat", label: strings.chat || "Chat", icon: "chat", color: props.isInternal ? KJUUP_COLORS.internal : KJUUP_COLORS.base_color },
    showEmailFilter ? { key: "email", label: strings.filter_email || "E-Mail", icon: "mail", color: KJUUP_COLORS.email } : null,
    hasTeamsItems ? { key: "teams", label: strings.pro_messages_tag_teams || "Teams", logo: teamsLogo } : null,
  ].filter(Boolean);
  const activeSourceTab = sourceTabs.find((tab) => tab.key === sourceFilter) || sourceTabs[0];
  const selectToggleDisabled = jobRunning || (!isSelect && !(visibleItems.length > 0));
  const selectToggleLabel = isSelect ? strings.cancel || "Cancel" : strings.select_files || "Select";

  const selectSource = (key) => {
    setSourceMenuOpen(false);
    setSourceFilter(key);
    if (jobRunning) return;
    setIsSelect(false);
    setSelectedItems([]);
  };

  const toggleSelectMode = () => {
    if (jobRunning) return;
    dismissMediaDownload(downloadScopeId);
    setSelectedItems([]);
    setIsSelect(!isSelect);
    setChatMedia((prev) =>
      prev.map((sec) => ({
        ...sec,
        items: (sec.items || []).map((row) => ({
          ...row,
          isSelect: false,
        })),
      }))
    );
    setEmailItems((prev) =>
      prev.map((row) => ({ ...row, isSelect: false }))
    );
  };

  return (
    <div
      className={`building-media-docs${useEmailTheme ? " media-docs-email-theme" : ""}${props.hideBack ? " building-media-docs--inline" : ""}`}
      style={{
        "--kjuup-brand": accent,
        "--kjuup-selection-border": accent,
        "--kjuup-selection-light":
          useEmailTheme && sourceFilter === "email"
            ? "rgba(224, 102, 237, 0.08)"
            : props.isInternal
              ? "rgba(var(--kjuup-internal-rgb), 0.07)"
              : "rgba(var(--kjuup-accent-rgb), 0.07)",
      }}
    >
      {!viewDoc && (
        <div className="building-media-docs__main">
          <div className="media_tabbing flex-shrink-0">
            <div className="building-media-docs__filter-bar">
              {!props.hideBack && (
              <button
                onClick={() => props.handleEditClose()}
                className={`media-back-btn kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral${props.isInternal ? ' kj-btn--internal' : ''}`}
              >
                <span
                  className="material-symbols-outlined building-media-docs__back-icon"
                >
                  keyboard_arrow_left
                </span>
              </button>
              )}
              <div className="kj-segmented" role="tablist">
                {typeTabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={value === tab.key}
                    className={`kj-segment${value === tab.key ? " is-active" : ""}`}
                    onClick={() => handleChange(null, tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="building-media-docs__filter-actions">
                {showSourceTabs && (
                  <div className="building-media-docs__source" ref={sourceMenuRef}>
                    <button
                      type="button"
                      className="kj-btn kj-btn--secondary kj-btn--sm building-media-docs__source-trigger"
                      onClick={() => setSourceMenuOpen((open) => !open)}
                      aria-haspopup="menu"
                      aria-expanded={sourceMenuOpen}
                    >
                      <span className="building-media-docs__source-prefix">
                        {strings.media_filter_source || "Source"}:
                      </span>
                      <span>{activeSourceTab.label}</span>
                      <MaterialIcon icon={sourceMenuOpen ? "expand_less" : "expand_more"} size={16} />
                    </button>
                    {sourceMenuOpen && (
                      <div className="new-message-type-selector building-media-docs__source-menu" role="menu">
                        {sourceTabs.map((tab) => (
                          <div
                            key={tab.key}
                            className="new-message-type-option"
                            role="menuitemradio"
                            aria-checked={sourceFilter === tab.key}
                            onClick={() => selectSource(tab.key)}
                          >
                            {tab.logo ? (
                              <img src={tab.logo} alt="" className="new-message-type-logo" />
                            ) : (
                              <span className="material-symbols-outlined new-message-type-icon" style={{ color: tab.color }}>
                                {tab.icon}
                              </span>
                            )}
                            <span className="building-media-docs__source-option-label">{tab.label}</span>
                            {sourceFilter === tab.key && (
                              <MaterialIcon icon="check" size={18} className="building-media-docs__source-check" />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {!showEmailUpsell && (
                  <button
                    type="button"
                    className={`kj-btn kj-btn--secondary kj-btn--icon building-media-docs__select-toggle${isSelect ? " is-active" : ""}`}
                    onClick={toggleSelectMode}
                    disabled={selectToggleDisabled}
                    aria-pressed={isSelect}
                    title={selectToggleLabel}
                    aria-label={selectToggleLabel}
                  >
                    <MaterialIcon icon={isSelect ? "close" : "library_add_check"} size={20} fill={isSelect ? 0 : 1} />
                  </button>
                )}
              </div>
            </div>

            {!showEmailUpsell && !props.hideAutoSync && (
              <MediaAutoSyncSettings
                scopeId={downloadScopeId}
                kind="building"
                targetId={props.project_id}
                accent={accent}
                isInternal={props.isInternal}
                emailTheme={useEmailTheme}
              />
            )}
            {!showEmailUpsell && (
              <MediaSelectionBar
                isSelect={isSelect}
                selectedCount={SelectedItems.length}
                isDownloading={jobRunning}
                accent={accent}
                isInternal={props.isInternal}
                emailTheme={useEmailTheme}
                strings={strings}
                language={language}
                visibleCount={visibleItems.length}
                allVisibleSelected={allVisibleSelected}
                downloadJob={downloadJob.matches ? downloadJob : null}
                onDismissNote={() => dismissMediaDownload(downloadScopeId)}
                hideSelectToggle
                onToggleSelect={toggleSelectMode}
                onToggleSelectAll={toggleSelectAll}
                onDownload={download}
              />
            )}
          </div>

          {showEmailUpsell ? (
            <div className="building-media-docs__upsell">
              <TrialWarningBanner />
              <EmailEmptyState
                variant={hasUsedTrial ? "trialEnded" : "noPlan"}
                onPrimary={() => leaveRoomsAndNavigate("/subscription")}
                style={{ background: "var(--kjuup-bg)", flex: 1 }}
              />
            </div>
          ) : (
            <div
              className="wscroll media-docs-scroll building-media-docs__scroll"
            >
              {emailFeatureEnabled && hasActiveKjuupIoPlan && (
                <TrialWarningBanner style={{ margin: "8px 12px 0" }} />
              )}
              {value === 2 && (
                media.some((section) => (section.items || []).some((item) => itemMatchesMediaTab(item, 2))) ? (
                  <div className="media-section-container building-media-docs__all">
                    {media.map((section, mainIndex) => {
                      const onCheck = (v, itemIndex) => _onCheckBoxPress(v, itemIndex, mainIndex);
                      return (
                        <React.Fragment key={`${section.createdAt}-${mainIndex}`}>
                          <div className="date-header image-section__date">{section.createdAt}</div>
                          <ImageSection
                            media={[section]}
                            viewerMedia={media}
                            hideDateHeaders
                            hideEmpty
                            props={props}
                            handleScroll={() => {}}
                            _onCheckBoxPress={onCheck}
                            isSelect={isSelect || jobOnThisTab}
                            downloadActive={jobOnThisTab}
                            selectionLocked={jobOnThisTab}
                            accentColor={accent}
                            onOpenEmailItem={openEmailItem}
                            apiBaseUrl={API_ROOT}
                          />
                          <DocsSection
                            media={[section]}
                            hideDateHeaders
                            hideEmpty
                            tiles
                            handleScroll={() => {}}
                            props={props}
                            isSelect={isSelect || jobOnThisTab}
                            setDoc={setDoc}
                            setViewDoc={setViewDoc}
                            _onCheckBoxPress={onCheck}
                            downloadActive={jobOnThisTab}
                            selectionLocked={jobOnThisTab}
                            accentColor={accent}
                            onOpenEmailItem={openEmailItem}
                          />
                        </React.Fragment>
                      );
                    })}
                  </div>
                ) : (
                  <div className="media-section-container media-empty-wrap">
                    <div className="no-media" role="status">
                      <span className="media-empty-label">
                        {strings.no_media_available || "No files"}
                      </span>
                    </div>
                  </div>
                )
              )}
              {value === 0 && (
                <ImageSection
                  media={media}
                  props={props}
                  handleScroll={() => {}}
                  _onCheckBoxPress={_onCheckBoxPress}
                  isSelect={isSelect || jobOnThisTab}
                  downloadActive={jobOnThisTab}
                  selectionLocked={jobOnThisTab}
                  accentColor={accent}
                  onOpenEmailItem={openEmailItem}
                  apiBaseUrl={API_ROOT}
                />
              )}
              {value === 1 && (
                <DocsSection
                  handleScroll={() => {}}
                  media={media}
                  props={props}
                  isSelect={isSelect || jobOnThisTab}
                  setDoc={setDoc}
                  setViewDoc={setViewDoc}
                  _onCheckBoxPress={_onCheckBoxPress}
                  downloadActive={jobOnThisTab}
                  selectionLocked={jobOnThisTab}
                  accentColor={accent}
                  onOpenEmailItem={openEmailItem}
                />
              )}
              {emailLoading && sourceFilter === "email" && media.length === 0 && (
                <div className="building-media-docs__loading">
                  {strings.email_loading || "Loading..."}
                </div>
              )}
            </div>
          )}

          {ViewerImages && ViewerImages.length > 0 && (
            <div className="building-media-docs__viewer-overlay">
              <FullScreenImageViewer reduxImages={ViewerImages} />
            </div>
          )}

          {emailPreview && (
            <AttachmentPreviewModal
              attachment={emailPreview}
              apiBaseUrl={API_ROOT}
              onClose={() => setEmailPreview(null)}
            />
          )}
        </div>
      )}
      {viewDoc && (
        <div className="building-media-docs__doc-viewer">
          <DocViewer
            chat={doc}
            handleClose={() => setViewDoc(false)}
            isInternal={props.isInternal}
          />
        </div>
      )}
    </div>
  );
}
